import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import blogData from "../data/blogData";

// Import all blog components
import ProactiveAssistance from "./blogs/ProactiveAssistance";
import Chatlet from "./blogs/Chatlet";
import Explorables from "./blogs/Explorables";
import Jelly from "./blogs/Jelly";
import Dissertation from "./blogs/Dissertation";

// Map component names to actual components
const blogComponents = {
  ProactiveAssistance: ProactiveAssistance,
  Chatlet: Chatlet,
  Explorables: Explorables,
  Jelly: Jelly,
  Dissertation: Dissertation,
  // Add more blog components here as you create them
  // AnotherBlog: AnotherBlog,
};

const BlogPost = () => {
  const { id } = useParams();
  const blog = blogData.find((b) => b.id === id);
  const [showTitleInHeader, setShowTitleInHeader] = useState(false);
  const titleRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (titleRef.current) {
        const titleBottom = titleRef.current.getBoundingClientRect().bottom;
        // Show title in header when the title has scrolled past the header (approximately 80px)
        setShowTitleInHeader(titleBottom < 80);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Report to Umami once per visit when the reader reaches the end of the post
  useEffect(() => {
    const end = endRef.current;
    if (!end) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        window.umami?.track("read-complete", { post: id });
        observer.disconnect();
      }
    });
    observer.observe(end);
    return () => observer.disconnect();
  }, [id]);

  if (!blog) {
    return (
      <div className="blog-post">
        <Helmet>
          <title>Blog Post Not Found - Yining "Rima" Cao</title>
        </Helmet>
        <div className="blog-post-header">
          <div className="blog-post-header-content">
            <Link to="/" className="blog-back-link">
              ← Back to Home
            </Link>
          </div>
        </div>
        <div className="blog-post-content">
          <h1 className="blog-post-title">Blog Post Not Found</h1>
          <p>The blog post you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  // Get the blog component
  const BlogComponent = blogComponents[blog.component];

  // Construct full URL for Open Graph tags
  const siteUrl = "https://rrrima.me";
  const blogUrl = `${siteUrl}/${blog.section || "blog"}/${blog.id}`;
  const imageUrl = blog.thumbnail
    ? `${siteUrl}${blog.thumbnail}`
    : `${siteUrl}/logo4.png`;

  return (
    <div className="blog-post">
      <Helmet>
        <title>{blog.title} - Yining "Rima" Cao</title>
        <meta name="description" content={blog.excerpt} />

        {/* Open Graph tags for Facebook, LinkedIn, etc. */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:url" content={blogUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="article:published_time" content={blog.date} />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.excerpt} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      <div
        className={`blog-post-header ${showTitleInHeader ? "with-title" : ""}`}
      >
        <div className="blog-post-header-content">
          <Link to="/" className="blog-back-link">
            ← Back to Home
          </Link>
          {showTitleInHeader && (
            <h2 className="blog-post-header-title">{blog.title}</h2>
          )}
        </div>
      </div>
      <div className="blog-post-content">
        <p className="blog-post-date">{blog.date}</p>
        <h1 ref={titleRef} className="blog-post-title">
          {blog.title}
        </h1>
        {blog.thumbnail && (
          <div className="blog-post-thumbnail">
            <img src={blog.thumbnail} alt={blog.title} />
          </div>
        )}
        <div className="blog-post-body">
          {BlogComponent ? (
            <BlogComponent />
          ) : (
            <p>Blog content coming soon...</p>
          )}
        </div>
        <div ref={endRef} aria-hidden="true" />
      </div>
    </div>
  );
};

export default BlogPost;
